import React, { useState, useEffect } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, TextInput, Alert, Share, Clipboard, Linking, Platform } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../App';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Player, RuleSet } from '../types';
import { useGame } from '../hooks/useGame';
import { gameService } from '../services/gameService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import RuleSetSelector from '../components/RuleSetSelector';

type Props = NativeStackScreenProps<RootStackParamList, 'Home'>;

type SetupStep = 'initial' | 'join';

export default function HomeScreen({ navigation }: Props) {
  const [setupStep, setSetupStep] = useState<SetupStep>('initial');
  const [playerName, setPlayerName] = useState('');
  const [gameId, setGameId] = useState<string>('');
  const [currentGameId, setCurrentGameId] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(true);
  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSet>('international');
  const [isNameValid, setIsNameValid] = useState(true);
  const { joinGame, error } = useGame(gameId);
  const [isCreatingGame, setIsCreatingGame] = useState(false);

  useEffect(() => {
    const loadCurrentGameId = async () => {
      try {
        const savedGameId = await AsyncStorage.getItem('currentGameId');
        if (savedGameId) {
          setCurrentGameId(savedGameId);
        }
      } catch (err) {
        console.error('Error loading game ID:', err);
      }
    };

    loadCurrentGameId();
  }, []);

  useEffect(() => {
    const testConnection = async () => {
      try {
        const isConnected = await gameService.testConnection();
        if (!isConnected) {
          Alert.alert('Connection Error', 'Could not connect to the game server. Please check your internet connection.');
        }
      } catch (err) {
        Alert.alert('Connection Error', 'Could not connect to the game server. Please check your internet connection.');
      } finally {
        setIsConnecting(false);
      }
    };

    testConnection();
  }, []);

  const handleShare = async () => {
    if (!currentGameId) return;
    
    try {
      await Share.share({
        message: `Join my Speaky Spokey game with code: ${currentGameId}`,
      });
    } catch (error) {
      console.error('Error sharing game code:', error);
    }
  };

  const handleCreateGame = async () => {
    if (isCreatingGame) return; // Prevent double-clicks
    
    setIsCreatingGame(true);
    try {
      console.log('Starting game creation process...');
      
      // Clear any existing game data
      await AsyncStorage.multiRemove(['currentGameId', 'currentPlayerId']);
      console.log('Cleared existing game data');
      
      // Create new player ID
      const playerId = `player-${Date.now()}`;
      console.log('Generated player ID:', playerId);
      
      // Create the game with a default reader name
      console.log('Creating game with player ID:', playerId);
      const newGameId = await gameService.createGame(playerId, 'Reader');
      console.log('Game created with ID:', newGameId);
      
      if (!newGameId) {
        throw new Error('Failed to create game - no game ID returned');
      }
      
      // Save the game data immediately
      const storageData: [string, string][] = [
        ['currentGameId', newGameId],
        ['currentPlayerId', playerId]
      ];
      console.log('Saving game data to storage:', storageData);
      await AsyncStorage.multiSet(storageData);
      console.log('Game data saved to storage');
      
      // Update local state
      setCurrentGameId(newGameId);
      
      // Navigate immediately to game screen
      console.log('Navigating to game screen with params:', { gameId: newGameId, playerId });
      navigation.replace('Game', { 
        gameId: newGameId, 
        playerId: playerId 
      });
      
      // Show success message after navigation
      Alert.alert(
        'Game Created!', 
        `Share this game code with other players: ${newGameId}`,
        [
          {
            text: 'Copy Code',
            onPress: async () => {
              await Clipboard.setString(newGameId);
              Alert.alert('Copied!', 'Game code copied to clipboard');
            }
          }
        ]
      );
      
    } catch (err) {
      console.error('Error creating game:', err);
      Alert.alert(
        'Error',
        'Failed to create game: ' + (err instanceof Error ? err.message : 'Unknown error'),
        [
          {
            text: 'Try Again',
            onPress: () => {
              console.log('Clearing game data after error');
              AsyncStorage.multiRemove(['currentGameId', 'currentPlayerId']);
            }
          }
        ]
      );
    } finally {
      setIsCreatingGame(false);
    }
  };

  const handleJoinGame = async () => {
    if (!playerName.trim()) {
      setIsNameValid(false);
      Alert.alert('Error', 'Please enter your name');
      return;
    }
    setIsNameValid(true);
    if (!gameId.trim()) {
      Alert.alert('Error', 'Please enter a game code');
      return;
    }

    try {
      const normalizedGameId = gameId.trim().toUpperCase();
      console.log('Attempting to join game:', normalizedGameId);
      
      // First verify the game exists
      const gameExists = await gameService.verifyGame(normalizedGameId);
      if (!gameExists) {
        Alert.alert('Error', 'Game not found. Please check the game code and try again.');
        return;
      }

      const playerId = `player-${Date.now()}`;
      const player: Player = {
        id: playerId,
        name: playerName.trim(),
        score: 0,
        isReader: false,
      };
      
      console.log('Joining as player:', player);
      try {
        await gameService.joinGame(normalizedGameId, player);
        console.log('Successfully joined game');
        
        // Save the game data
        await AsyncStorage.multiSet([
          ['currentGameId', normalizedGameId],
          ['currentPlayerId', playerId]
        ]);
        console.log('Game data saved to storage');
        
        // Navigate to game screen with params
        console.log('Navigating to game screen with params:', { gameId: normalizedGameId, playerId });
        navigation.replace('Game', {
          gameId: normalizedGameId,
          playerId: playerId
        });
      } catch (joinError) {
        if (joinError instanceof Error && joinError.message === 'Game has already started') {
          Alert.alert(
            'Game In Progress',
            'Sorry, this game has already started and is not accepting new players.',
            [
              {
                text: 'OK',
                onPress: () => {
                  setGameId('');
                  setSetupStep('initial');
                }
              }
            ]
          );
        } else {
          throw joinError;
        }
      }
    } catch (err) {
      console.error('Error joining game:', err);
      Alert.alert('Error', 'Failed to join game. Please try again.');
    }
  };

  const renderInitialStep = () => (
    <View style={styles.content}>
      <Text style={styles.title}>Speaky Spokey</Text>
      <Text style={styles.subtitle}>The Jamaican Word Game</Text>
      
      <View style={styles.buttonContainer}>
        {!gameId.trim() && (
          <>
            <TouchableOpacity 
              style={[styles.button, isCreatingGame && styles.disabledButton]}
              onPress={handleCreateGame}
              disabled={isCreatingGame}
            >
              <Text style={styles.buttonText}>
                {isCreatingGame ? 'Creating Game...' : 'Start New Game'}
              </Text>
            </TouchableOpacity>
            
            <Text style={styles.orText}>- OR -</Text>
          </>
        )}
        
        <View style={styles.quickJoinContainer}>
          <TextInput
            style={[styles.input, styles.quickJoinInput]}
            placeholder="Enter game code"
            value={gameId}
            onChangeText={setGameId}
            autoCapitalize="characters"
            placeholderTextColor="#666"
          />
          <TouchableOpacity 
            style={[styles.button, styles.quickJoinButton]}
            onPress={() => {
              if (gameId.trim()) {
                setSetupStep('join');
              } else {
                Alert.alert('Error', 'Please enter a game code');
              }
            }}
          >
            <Text style={styles.buttonText}>Join Game</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderJoinStep = () => (
    <View style={styles.content}>
      <Text style={styles.title}>Join Game</Text>
      
      <View style={styles.formContainer}>
        <Text style={[styles.sectionTitle, !isNameValid && styles.invalidFieldLabel]}>
          Your Name:
          {!isNameValid && <Text style={styles.requiredStar}> *</Text>}
        </Text>
        <TextInput
          style={[
            styles.input,
            !isNameValid && styles.invalidInput,
            styles.nameInput
          ]}
          placeholder="Enter your name"
          value={playerName}
          onChangeText={(text) => {
            setPlayerName(text);
            setIsNameValid(true);
          }}
          autoFocus
          placeholderTextColor={!isNameValid ? '#E63946' : '#666'}
        />
        {!isNameValid && (
          <Text style={styles.invalidText}>Please enter your name to join the game</Text>
        )}
        
        {!gameId.trim() && (
          <>
            <Text style={styles.sectionTitle}>Game Code:</Text>
            <TextInput
              style={styles.input}
              placeholder="Enter game code"
              value={gameId}
              onChangeText={setGameId}
              autoCapitalize="characters"
            />
          </>
        )}
        
        {gameId.trim() && (
          <View style={styles.gameCodeDisplay}>
            <Text style={styles.sectionTitle}>Game Code:</Text>
            <TouchableOpacity
              onPress={() => {
                Clipboard.setString(gameId.trim().toUpperCase());
                Alert.alert('Copied!', 'Game code copied to clipboard');
              }}
            >
              <Text style={styles.displayedGameCode}>{gameId.trim().toUpperCase()}</Text>
              <Text style={styles.copyHint}>Tap to copy</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setGameId('')}
              style={styles.changeCodeButton}
            >
              <Text style={styles.changeCodeText}>Change Code</Text>
            </TouchableOpacity>
          </View>
        )}
        
        <View style={styles.buttonContainer}>
          <TouchableOpacity 
            style={styles.button}
            onPress={handleJoinGame}
          >
            <Text style={styles.buttonText}>Join Game</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.button, styles.secondaryButton]}
            onPress={() => {
              setSetupStep('initial');
              setGameId('');
              setPlayerName('');
            }}
          >
            <Text style={[styles.buttonText, styles.secondaryButtonText]}>Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderGameHeader = () => {
    console.log('Current game ID:', currentGameId);
    
    return (
      <View style={styles.headerContainer}>
        <Text style={styles.gameCodeLabel}>GAME CODE</Text>
        <View style={styles.gameCodeBox}>
          <Text style={styles.gameCode}>
            {currentGameId ? currentGameId.toUpperCase() : 'Loading...'}
          </Text>
          <TouchableOpacity 
            style={[
              styles.shareButton,
              !currentGameId && styles.disabledButton
            ]}
            onPress={handleShare}
            disabled={!currentGameId}
          >
            <Text style={styles.shareButtonText}>Share Code</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      {isConnecting ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Connecting to game server...</Text>
        </View>
      ) : (
        <>
          {setupStep === 'initial' && renderInitialStep()}
          {setupStep === 'join' && renderJoinStep()}

          {error && (
            <Text style={styles.errorText}>{error}</Text>
          )}
        </>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFD84D',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#E63946',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 18,
    color: '#1E4B8F',
    marginBottom: 40,
  },
  rulesContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    marginBottom: 40,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#1E4B8F',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  rulesTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#E63946',
  },
  rulesText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#1E4B8F',
    marginBottom: 20,
  },
  getCardsButton: {
    backgroundColor: '#1E4B8F',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 12,
  },
  getCardsText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  cardNote: {
    fontSize: 14,
    color: '#1E4B8F',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    gap: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E4B8F',
  },
  input: {
    borderWidth: 1,
    borderColor: '#1E4B8F',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    width: '100%',
    marginBottom: 15,
    backgroundColor: '#fff',
  },
  buttonContainer: {
    gap: 15,
    width: '100%',
    marginTop: 10,
  },
  button: {
    backgroundColor: '#1E4B8F',
    padding: 15,
    borderRadius: 25,
    alignItems: 'center',
    width: '100%',
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#1E4B8F',
  },
  secondaryButtonText: {
    color: '#1E4B8F',
  },
  orText: {
    textAlign: 'center',
    color: '#666',
    fontWeight: 'bold',
    marginVertical: 10,
  },
  errorText: {
    color: '#ff4444',
    textAlign: 'center',
    marginTop: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  gameCodeLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 10,
  },
  gameCodeBox: {
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    })
  },
  gameCode: {
    fontSize: 16,
    color: '#333',
  },
  shareButton: {
    backgroundColor: '#1E4B8F',
    padding: 10,
    borderRadius: 5,
    marginLeft: 10,
  },
  disabledButton: {
    backgroundColor: '#ddd',
  },
  shareButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  quickJoinContainer: {
    width: '100%',
    gap: 10,
  },
  quickJoinInput: {
    backgroundColor: '#fff',
    marginBottom: 0,
    textAlign: 'center',
    fontSize: 20,
    letterSpacing: 2,
    borderColor: '#1E4B8F',
    borderWidth: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    })
  },
  quickJoinButton: {
    marginTop: 0,
  },
  gameCodeDisplay: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    borderColor: '#1E4B8F',
    borderWidth: 1,
  },
  displayedGameCode: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#E63946',
    textAlign: 'center',
    letterSpacing: 2,
    marginVertical: 10,
  },
  changeCodeButton: {
    alignItems: 'center',
  },
  changeCodeText: {
    color: '#1E4B8F',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  copyHint: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    marginTop: 4,
  },
  invalidFieldLabel: {
    color: '#E63946',
  },
  invalidInput: {
    borderColor: '#E63946',
    backgroundColor: '#FFF0F0',
  },
  invalidText: {
    color: '#E63946',
    fontSize: 12,
    marginTop: -10,
    marginBottom: 15,
  },
  requiredStar: {
    color: '#E63946',
    fontWeight: 'bold',
  },
  nameInput: {
    fontSize: 18,
    textAlign: 'center',
    fontWeight: '500',
  },
}); 